import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Archive, ArrowRight, Box, CheckCircle2, Circle, Clock, HardDrive, HelpCircle, PackagePlus, Settings,
  type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { COLORS, type ColorKey } from '@/help/diagrams/svg-primitives'
import type { SetupTopic } from './parse'
import { Guide, StepMd } from './Guide'
import { guideKey, readDone } from './guideProgress'
import { WalkthroughPlayer } from '@/help/WalkthroughPlayer'
import { WALKTHROUGHS } from '@/help/walkthroughs'

const ICONS: Record<string, LucideIcon> = {
  archive: Archive, box: Box, clock: Clock, 'hard-drive': HardDrive, package: PackagePlus, settings: Settings,
}

/** The open question is in the URL, so a link can land on one. */
const PARAM = 'setup'

const shortName = (t: SetupTopic) => t.attrs.short ?? t.title

function storedDone(topic: SetupTopic): number {
  return readDone(localStorage.getItem(guideKey(topic.title)), topic.steps.map(s => s.title)).size
}

function TopicIcon({ topic, className }: { topic: SetupTopic; className?: string }) {
  const Icon = ICONS[topic.attrs.icon ?? ''] ?? HelpCircle
  const c = topic.attrs.tone && topic.attrs.tone in COLORS ? COLORS[topic.attrs.tone as ColorKey] : COLORS.neutral
  return (
    <span
      className={cn('flex shrink-0 items-center justify-center rounded-lg border', className ?? 'h-9 w-9')}
      style={{ background: c.bg, borderColor: c.stroke, color: c.stroke }}
      aria-hidden='true'
    >
      <Icon className='h-4 w-4' />
    </span>
  )
}

type NodeState = 'done' | 'next' | 'open' | 'waiting'

const NODE_LABEL: Record<NodeState, string> = { done: 'Done', next: 'Next up', open: 'Ready', waiting: 'Do earlier steps first' }

function JourneyNode({ topic, n, state, active, onClick }: {
  topic: SetupTopic
  n?: number
  state: NodeState
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type='button'
      onClick={onClick}
      aria-current={active ? 'step' : undefined}
      className={cn(
        'flex min-w-[118px] items-center gap-2 rounded-xl border px-2.5 py-2 text-left transition-colors hover:border-primary/40',
        state === 'next' && 'border-primary bg-primary/5',
        state === 'done' && 'border-emerald-500/40 bg-emerald-500/5',
        active && 'ring-2 ring-primary/30',
      )}
    >
      <span
        className={cn(
          'flex h-7 w-7 shrink-0 items-center justify-center rounded-full border text-[12px] font-semibold',
          state === 'done' ? 'border-emerald-500 bg-emerald-500 text-white'
            : state === 'next' ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border text-muted-foreground',
        )}
      >
        {state === 'done' ? <CheckCircle2 className='h-4 w-4' /> : n ?? <TopicIcon topic={topic} className='h-4 w-4 border-0 bg-transparent' />}
      </span>
      <span className='min-w-0'>
        <span className='block text-[13px] font-semibold leading-tight text-foreground'>{shortName(topic)}</span>
        <span className={cn('block text-[11px]', state === 'next' ? 'text-primary' : state === 'done' ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground')}>
          {NODE_LABEL[state]}
        </span>
      </span>
    </button>
  )
}

/**
 * A ```setup fence: "What do you want to set up?" as a set of question cards.
 * Picking one shows what has to be done first (ticked once that question's
 * guide is finished) and then that question's own step-by-step guide.
 */
export function SetupAssistant({ intro, topics }: { intro: string; topics: SetupTopic[] }) {
  const [params, setParams] = useSearchParams()
  const [progress, setProgress] = useState<Record<string, number>>(() =>
    Object.fromEntries(topics.map(t => [t.id, storedDone(t)])),
  )
  const panelRef = useRef<HTMLDivElement>(null)
  const byId = useMemo(() => new Map(topics.map(t => [t.id, t])), [topics])
  const selected = byId.get(params.get(PARAM) ?? '') ?? null
  const isDone = (t: SetupTopic) => (progress[t.id] ?? 0) >= t.steps.length && t.steps.length > 0

  const open = (id: string) => {
    const next = new URLSearchParams(params)
    next.set(PARAM, id)
    setParams(next, { replace: true })
  }
  const [scrollTo, setScrollTo] = useState(0)
  const choose = (id: string) => {
    open(id)
    setScrollTo(n => n + 1)
  }
  useEffect(() => {
    if (scrollTo) panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [scrollTo])

  const selectedId = selected?.id
  const onProgress = useCallback(
    (done: number) => {
      if (!selectedId) return
      setProgress(prev => (prev[selectedId] === done ? prev : { ...prev, [selectedId]: done }))
    },
    [selectedId],
  )

  // The journey is every question in order; "Any time" ones run alongside it.
  const journey = topics.filter(t => !t.attrs.badge || !/any time/i.test(t.attrs.badge))
  const alongside = topics.filter(t => !journey.includes(t))
  const ready = (t: SetupTopic) => t.needs.every(id => !byId.has(id) || isDone(byId.get(id)!))
  const nextUp = journey.find(t => !isDone(t) && ready(t)) ?? journey.find(t => !isDone(t))

  const nextTopic = selected && isDone(selected)
    ? topics.slice(topics.indexOf(selected) + 1).find(t => !isDone(t)) ?? topics.find(t => !isDone(t))
    : undefined

  return (
    <div className='not-prose my-6'>
      <div className='mb-4 rounded-2xl border bg-card p-5 shadow-sm'>
        <div className='flex flex-wrap items-baseline justify-between gap-2'>
          <div className='text-base font-semibold text-foreground'>Your setup journey</div>
          <div className='text-[12px] text-muted-foreground'>
            {nextUp ? <>Next up: <strong className='text-foreground'>{shortName(nextUp)}</strong></> : 'Everything is set up'}
          </div>
        </div>
        <div className='mt-4 flex flex-wrap items-stretch gap-y-3'>
          {journey.map((t, i) => (
            <div key={t.id} className='flex items-center'>
              <JourneyNode
                topic={t}
                n={i + 1}
                state={isDone(t) ? 'done' : t === nextUp ? 'next' : ready(t) ? 'open' : 'waiting'}
                active={selected?.id === t.id}
                onClick={() => choose(t.id)}
              />
              {i < journey.length - 1 && <ArrowRight className='mx-1.5 h-4 w-4 shrink-0 text-muted-foreground/60' aria-hidden='true' />}
            </div>
          ))}
          {alongside.length > 0 && (
            <div className='flex items-center sm:ml-4 sm:border-l sm:pl-4'>
              <span className='mr-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>Any time</span>
              {alongside.map(t => (
                <JourneyNode
                  key={t.id}
                  topic={t}
                  state={isDone(t) ? 'done' : 'open'}
                  active={selected?.id === t.id}
                  onClick={() => choose(t.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <div className='rounded-2xl border bg-card p-5 shadow-sm'>
        <div className='text-base font-semibold text-foreground'>{intro || 'What do you want to set up?'}</div>
        <div className='mt-0.5 text-[13px] text-muted-foreground'>
          Pick a question. Each one says what has to exist first, then shows the setup one step at a time.
        </div>

        <div className='mt-4 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3'>
          {topics.map((t, i) => {
            const done = progress[t.id] ?? 0
            const finished = isDone(t)
            const blocked = t.needs.filter(id => byId.has(id) && !isDone(byId.get(id)!))
            return (
              <button
                key={t.id}
                type='button'
                onClick={() => choose(t.id)}
                aria-pressed={selected?.id === t.id}
                className={cn(
                  'group flex flex-col rounded-xl border bg-background p-3.5 text-left transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-md',
                  selected?.id === t.id && 'border-primary ring-2 ring-primary/20',
                )}
              >
                <div className='flex items-start gap-3'>
                  <TopicIcon topic={t} />
                  <div className='min-w-0 flex-1'>
                    <div className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
                      {t.attrs.badge ?? `Step ${i + 1}`}
                    </div>
                    <div className='text-[14px] font-semibold leading-snug text-foreground'>{t.title}</div>
                  </div>
                  {finished && <CheckCircle2 className='h-5 w-5 shrink-0 text-emerald-600' aria-label='Done' />}
                </div>
                {t.attrs.meta && <div className='mt-2 text-[12.5px] leading-snug text-muted-foreground'>{t.attrs.meta}</div>}
                <div className='mt-auto pt-3'>
                  {blocked.length > 0 && !finished && (
                    <div className='mb-1 text-[11px] font-medium text-amber-700 dark:text-amber-400'>
                      Do first: {blocked.map(id => shortName(byId.get(id)!)).join(', ')}
                    </div>
                  )}
                  <div className='text-[11px] text-muted-foreground'>
                    {finished ? 'Done' : done > 0 ? `${done} of ${t.steps.length} steps done` : `${t.steps.length} steps`}
                  </div>
                  <div className='mt-1 h-1 overflow-hidden rounded-full bg-muted'>
                    <div
                      className='h-full rounded-full bg-emerald-500 transition-all'
                      style={{ width: `${t.steps.length ? Math.round((done / t.steps.length) * 100) : 0}%` }}
                    />
                  </div>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      <div ref={panelRef} className='scroll-mt-4'>
        {selected ? (
          <div className='mt-5'>
            <div className='flex items-start gap-3'>
              <TopicIcon topic={selected} className='h-11 w-11' />
              <div className='min-w-0'>
                <div className='text-xl font-semibold leading-tight text-foreground'>{selected.title}</div>
                {selected.attrs.meta && <div className='mt-0.5 text-sm text-muted-foreground'>{selected.attrs.meta}</div>}
              </div>
            </div>

            {selected.intro && (
              <div className='mt-3 text-[14px] leading-relaxed text-foreground/90'>
                <StepMd>{selected.intro}</StepMd>
              </div>
            )}

            {selected.attrs.video && WALKTHROUGHS[selected.attrs.video] && (
              <div className='mt-4'>
                <div className='mb-1 text-[13px] font-semibold text-foreground'>Watch it done once, then follow the steps below</div>
                <WalkthroughPlayer name={selected.attrs.video} />
              </div>
            )}

            <div className='mt-4 rounded-xl border bg-muted/30 p-4'>
              <div className='text-[13px] font-semibold text-foreground'>Before you start</div>
              {selected.needs.length === 0 ? (
                <div className='mt-1 flex items-center gap-2 text-[13px] text-muted-foreground'>
                  <CheckCircle2 className='h-4 w-4 text-emerald-600' /> Nothing has to be set up first. You can start straight away.
                </div>
              ) : (
                <div className='mt-2 space-y-1.5'>
                  {selected.needs.map(id => {
                    const need = byId.get(id)
                    if (!need) return null
                    const ok = isDone(need)
                    return (
                      <div key={id} className='flex flex-wrap items-center gap-2 text-[13px]'>
                        {ok
                          ? <CheckCircle2 className='h-4 w-4 shrink-0 text-emerald-600' />
                          : <Circle className='h-4 w-4 shrink-0 text-amber-500' />}
                        <span className={ok ? 'text-muted-foreground' : 'text-foreground'}>{need.title}</span>
                        {ok ? (
                          <span className='text-xs text-emerald-700 dark:text-emerald-400'>done</span>
                        ) : (
                          <button
                            type='button'
                            onClick={() => choose(id)}
                            className='flex items-center gap-1 text-xs font-medium text-primary hover:underline'
                          >
                            Not done yet: set it up first <ArrowRight className='h-3 w-3' />
                          </button>
                        )}
                      </div>
                    )
                  })}
                  <div className='pt-1 text-[12px] text-muted-foreground'>
                    Already set up in your site? Open that question and tick its steps, or just carry on.
                  </div>
                </div>
              )}
            </div>

            <Guide key={selected.id} title={selected.title} items={selected.steps} label='Step by step' onProgress={onProgress} />

            {nextTopic && (
              <button
                type='button'
                onClick={() => choose(nextTopic.id)}
                className='flex w-full items-center gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4 text-left hover:bg-emerald-500/10'
              >
                <CheckCircle2 className='h-5 w-5 shrink-0 text-emerald-600' />
                <div className='min-w-0 flex-1 text-[13px]'>
                  <div className='font-semibold text-foreground'>This part is done.</div>
                  <div className='text-muted-foreground'>Next: {nextTopic.title}</div>
                </div>
                <ArrowRight className='h-4 w-4 text-emerald-700' />
              </button>
            )}
          </div>
        ) : (
          <div className='mt-4 rounded-xl border border-dashed p-5 text-center text-[13px] text-muted-foreground'>
            Pick a question above to see its steps. Setting up from scratch? Start with <strong>Step 1</strong> and work left to right.
          </div>
        )}
      </div>
    </div>
  )
}
